import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const REQUIRED_FIELDS = ['title', 'author', 'description', 'price', 'category'];

const parseCSV = (text) => {
  const lines = text?.trim()?.split('\n');
  if (lines?.length < 2) return { data: [], errors: ['CSV must have a header row and at least one data row'] };
  const headers = lines?.[0]?.split(',')?.map((h) => h?.trim()?.replace(/^"|"$/g, ''));
  const data = [];
  const errors = [];
  for (let i = 1; i < lines?.length; i++) {
    const values = lines?.[i]?.split(',')?.map((v) => v?.trim()?.replace(/^"|"$/g, ''));
    const row = {};
    headers?.forEach((h, idx) => { row[h] = values?.[idx] || ''; });
    data?.push(row);
  }
  return { data, errors };
};

const parseJSON = (text) => {
  try {
    let parsed = JSON.parse(text);
    const data = Array.isArray(parsed) ? parsed : [parsed];
    return { data, errors: [] };
  } catch (e) {
    return { data: [], errors: ['Invalid JSON format: ' + e?.message] };
  }
};

const validateBooks = (books) => {
  const valid = [];
  const invalid = [];
  books?.forEach((book, idx) => {
    const missing = REQUIRED_FIELDS?.filter((f) => !book?.[f]);
    if (missing?.length > 0) {
      invalid?.push({ row: idx + 1, book, reason: `Missing fields: ${missing?.join(', ')}` });
    } else {
      valid?.push({
        ...book,
        price: parseFloat(book?.price) || 0,
        is_new: book?.is_new === 'true' || book?.is_new === true,
        is_bestseller: book?.is_bestseller === 'true' || book?.is_bestseller === true,
        frameworks: typeof book?.frameworks === 'string' ? book?.frameworks?.split(';')?.map((f) => f?.trim())?.filter(Boolean)
          : (Array.isArray(book?.frameworks) ? book?.frameworks : []),
        cover_image: book?.cover_image || '',
        cover_image_alt: book?.cover_image_alt || `${book?.title} book cover`,
      });
    }
  });
  return { valid, invalid };
};

const BulkUploadPanel = ({ onUpload, isUploading, onClose }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e?.target?.result;
      let parsed;
      if (file?.name?.endsWith('.json')) {
        parsed = parseJSON(text);
      } else {
        parsed = parseCSV(text);
      }
      const { valid, invalid } = validateBooks(parsed?.data);
      setParseResult({ valid, invalid, parseErrors: parsed?.errors, fileName: file?.name });
    };
    reader?.readAsText(file);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    handleFile(file);
  };

  const handleFileInput = (e) => {
    handleFile(e?.target?.files?.[0]);
  };

  const handleUpload = async () => {
    if (!parseResult?.valid?.length) return;
    setUploadProgress(0);
    const batchSize = 5;
    const total = parseResult?.valid?.length;
    for (let i = 0; i < total; i += batchSize) {
      const batch = parseResult?.valid?.slice(i, i + batchSize);
      await onUpload?.(batch);
      setUploadProgress(Math.min(100, Math.round(((i + batchSize) / total) * 100)));
    }
  };

  const csvTemplate = `title,author,description,price,category,cover_image,cover_image_alt,external_link,is_new,is_bestseller,frameworks\n"Book Title","Author Name","Book description here",9.99,business,"/assets/images/cover.jpg","Book cover alt text","https://example.com",true,false,"Strategy;Leadership"`;

  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'books_template.csv';
    a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Bulk Upload Books</h2>
            <p className="text-sm text-muted-foreground mt-1">Import books via CSV or JSON file</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted transition-colors" aria-label="Close">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Drop Zone */}
          <div
            onDragOver={(e) => { e?.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef?.current?.click()}
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
              isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
            }`}
          >
            <Icon name="Upload" size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="font-medium text-foreground">Drop your CSV or JSON file here</p>
            <p className="text-sm text-muted-foreground mt-1">or click to browse</p>
            <p className="text-xs text-muted-foreground mt-3">Supported formats: .csv, .json</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.json"
              onChange={handleFileInput}
              className="hidden"
              aria-label="Upload file"
            />
          </div>

          {/* Template Download */}
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="FileText" size={18} className="text-muted-foreground" />
              <span className="text-sm text-foreground">Need a template?</span>
            </div>
            <button
              onClick={downloadTemplate}
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <Icon name="Download" size={14} />
              Download CSV Template
            </button>
          </div>

          {/* Parse Results */}
          {parseResult && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Icon name="FileCheck" size={18} className="text-success" />
                <span className="text-sm font-medium text-foreground">{parseResult?.fileName}</span>
              </div>

              {parseResult?.parseErrors?.length > 0 && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
                  {parseResult?.parseErrors?.map((err, i) => (
                    <p key={i} className="text-sm text-error">{err}</p>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-success/10 border border-success/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-success">{parseResult?.valid?.length}</p>
                  <p className="text-xs text-muted-foreground">Valid books ready to import</p>
                </div>
                <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-error">{parseResult?.invalid?.length}</p>
                  <p className="text-xs text-muted-foreground">Rows with errors</p>
                </div>
              </div>

              {parseResult?.invalid?.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {parseResult?.invalid?.map((item, i) => (
                    <div key={i} className="text-xs p-2 bg-error/5 rounded flex items-start gap-2">
                      <Icon name="AlertCircle" size={12} className="text-error mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">Row {item?.row}: {item?.reason}</span>
                    </div>
                  ))}
                </div>
              )}

              {isUploading && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <Button variant="outline" onClick={onClose} disabled={isUploading}>Cancel</Button>
            <Button
              onClick={handleUpload}
              disabled={!parseResult?.valid?.length || isUploading}
            >
              {isUploading ? (
                <span className="flex items-center gap-2">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Uploading...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Icon name="Upload" size={16} />
                  Import {parseResult?.valid?.length || 0} Books
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadPanel;
